//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import moment from 'moment-timezone';

import { SessionVarsService } from '@services/session-vars-service';

const DATE_RANGE = {
  ALL_TIME: 'AT',
  CUSTOM: 'C',
  LAST_3_MONTHS: '3M',
  LAST_24_HOURS: '24H',
  LAST_30_DAYS: '30D',
  LAST_WEEK: '7D',
  TODAY: 'T',
};

// Share the date range constants on the global curalate object,
// because legacy pages still need them in contexts without Angular loaded.
// misc is typed as required but may be absent if this module loads before
// js/curalate.js populates it — the guard is intentional.
// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
if (window.CURALATE?.misc) {
  window.CURALATE.misc.DATE_RANGE_CONSTANTS = DATE_RANGE;
}

interface IDateRange {
  from?: moment.Moment | string | number;
  to?: moment.Moment | string | number;
}

// DateTime/TimeZone utilities. Depends on moment.js library.
export class DateTimeService {
  constructor(private readonly SessionVarsService: SessionVarsService) {}

  public ISO_FORMAT_NO_TZ = 'YYYY-MM-DDTHH:mm:ss.SSS';
  public ISO_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSZ';

  public userZoneId = this.SessionVarsService.getTimeZoneId();

  // Returns a moment representing the input date in the user's time zone.
  public userMoment(date?: Date): moment.Moment;
  public userMoment(date: string, parseFormat?: string): moment.Moment;
  public userMoment(date?: Date | string, parseFormat?: string): moment.Moment {
    if (date) {
      if (typeof date === 'string') {
        return parseFormat
          ? moment.tz(date, parseFormat, this.userZoneId)
          : moment.tz(date, this.userZoneId);
      } else {
        return moment.tz(date, this.userZoneId);
      }
    } else {
      return moment.tz(this.userZoneId);
    }
  }

  // Returns a moment representing now in the user's time zone.
  public userNow() {
    return moment.tz(this.userZoneId).format();
  }

  public DateRangeConstants = DATE_RANGE;

  // Builds a dateRange query param from an object
  // The passed object should contain a "from" AND/OR a "to" field which can either be
  // moment objects, strings, or timestamps.
  public buildDateRangeParam(
    dateRangeObject: IDateRange,
    optDateSeparator?: string,
  ) {
    let from, to;
    const dateSeparator = optDateSeparator ?? '_';

    if (dateRangeObject.from) {
      if (!moment.isMoment(dateRangeObject.from)) {
        from = moment(new Date(dateRangeObject.from)).format('YYYY-MM-D');
      } else {
        from = dateRangeObject.from.format('YYYY-MM-D');
      }
    } else {
      from = '';
    }

    if (dateRangeObject.to) {
      if (!moment.isMoment(dateRangeObject.to)) {
        to = moment(new Date(dateRangeObject.to)).format('YYYY-MM-D');
      } else {
        to = dateRangeObject.to.format('YYYY-MM-D');
      }
    } else {
      to = '';
    }

    if (from.length === 0 && to.length === 0) {
      return '';
    } else {
      return from + dateSeparator + to;
    }
  }

  // Returns a moment representing the start of the day in the user's time zone
  public userToday() {
    return moment.tz(this.userZoneId).startOf('day');
  }

  // Returns a moment representing the start of the day, tomorrow, in the user's time zone
  public userTomorrow() {
    return moment.tz(this.userZoneId).startOf('day').add(1, 'days');
  }

  // Returns a moment representing the current hour
  public roundNow() {
    return moment().minutes(0).seconds(0).millisecond(0);
  }

  // Returns a moment with the same date components as the input date except with the browser's time zone component.
  public applyBrowserTimeZone(date: moment.Moment, parseFormat?: string) {
    const m = parseFormat ? moment(date, parseFormat) : moment(date),
      noTimeZoneString = m.format(this.ISO_FORMAT_NO_TZ);
    return moment(noTimeZoneString);
  }

  // Returns a moment with the same date components as the input date except with the user's time zone component.
  public applyUserTimeZone(date: moment.Moment, parseFormat?: string) {
    const m = parseFormat ? moment(date, parseFormat) : moment(date),
      noTimeZoneString = m.format(this.ISO_FORMAT_NO_TZ);
    return moment.tz(noTimeZoneString, this.userZoneId);
  }

  // Returns an formatted string.
  // If format not specified, uses an ISO format including milliseconds which is what our server serialization expects.
  public formatUserMoment(
    date?: string,
    format?: string,
    parseFormat?: string,
  ) {
    const userMomentOpt =
      date &&
      (parseFormat
        ? this.userMoment(date, parseFormat)
        : this.userMoment(date));
    if (!date) {
      return date;
    } else if (format && userMomentOpt) {
      return userMomentOpt.format(format);
    } else if (userMomentOpt) {
      return userMomentOpt.format(this.ISO_FORMAT);
    }
  }

  // Exposes time zone related session variables, so most components don't need to reference both dependencies
  public getFullTimeZoneName() {
    return this.SessionVarsService.getTimeZoneFullName();
  }
  public getShortTimeZoneName() {
    return this.SessionVarsService.getTimeZoneShortName();
  }
  public getTimeZoneId() {
    return this.SessionVarsService.getTimeZoneId();
  }

  // Returns the delta in hours between a given time and the current time
  public getHoursFromNow(date: string) {
    return Math.round(
      moment
        .duration(
          moment.tz(this.userZoneId).diff(moment.tz(date, this.userZoneId)),
        )
        .asHours(),
    );
  }

  // Returns the delta in days between a given day and the current day
  public getDaysFromNow(date: string) {
    const userStartOfToday = moment.tz(this.userZoneId).startOf('day');
    const userStartOfDate = moment.tz(date, this.userZoneId).startOf('day');
    return Math.round(
      moment.duration(userStartOfToday.diff(userStartOfDate)).asDays(),
    );
  }
}

angular
  .module('curalate')
  .service('DateTimeService', ['SessionVarsService', DateTimeService]);
